import { 
  Hotel, Car, Calendar, MapPin, Users, Globe, Shield, Zap, Target, 
  TrendingUp, Settings, Database, Utensils, Plane, Coffee, Star,
  CheckCircle, BarChart3, Clock, Lock
} from 'lucide-react';
import { ProductConfigs } from './types';

export const hospitalitySolutionsConfigs: ProductConfigs = {
  'hotel-management-suite': {
    keyBenefits: [
      {
        icon: Hotel,
        title: 'Intelligent Property Management',
        description: 'Advanced hotel operations with automated check-in/out, room assignment optimisation, and guest experience personalisation',
        metrics: '95% guest satisfaction',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Optimisation Engine',
        description: 'AI-powered revenue management with dynamic pricing, demand forecasting, and yield optimisation strategies',
        metrics: '35% revenue increase',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Guest Experience Platform',
        description: 'Personalised guest journey management with preference tracking, service automation, and loyalty integration',
        metrics: 'Personalised service excellence',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Calendar,
        title: 'Advanced Reservation System',
        description: 'Sophisticated booking engine with group reservations, waitlist management, and real-time availability across all channels',
        metrics: '99% booking accuracy',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Star,
        title: 'Guest Satisfaction Intelligence',
        description: 'Real-time guest feedback analysis with sentiment monitoring, service recovery alerts, and reputation management',
        metrics: '4.8/5 guest rating average',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: BarChart3,
        title: 'Business Intelligence Dashboard',
        description: 'Comprehensive analytics with occupancy forecasting, ADR optimisation, and competitive benchmarking insights',
        metrics: 'Strategic decision support',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Integrated PMS & Channel Manager',
        description: 'Comprehensive property management with multi-channel distribution and inventory synchronisation',
        metrics: 'Unified operations control',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Housekeeping & Maintenance Hub',
        description: 'Intelligent housekeeping scheduling with maintenance management and quality assurance tracking',
        metrics: 'Operational efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Property Operations',
        description: 'Centralised management for hotel chains with consolidated reporting and standardised operations',
        metrics: 'Chain-wide consistency',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Automated Guest Services',
        description: 'AI-powered concierge services with automated upselling, local recommendations, and service request management',
        metrics: '60% service automation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: CheckCircle,
        title: 'Food & Beverage Integration',
        description: 'Complete F&B management with POS integration, inventory control, and cost analysis for hotel restaurants',
        metrics: 'F&B profit optimisation',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Mobile Guest Experience',
        description: 'Contactless mobile check-in/out with digital keys, mobile payments, and in-room service requests',
        metrics: 'Contactless excellence',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive SOC 2, ISO 27001, and GDPR compliance with hospitality-specific data protection standards',
        metrics: 'Regulatory compliance',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat intelligence with performance monitoring and security posture optimization',
        metrics: 'Performance monitoring',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform with unlimited scalability and global deployment capabilities',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: 'Advanced protection',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart cards, and adaptive access controls',
        metrics: 'Identity security',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with automated threat detection, incident response, and forensic analysis',
        metrics: 'Threat detection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Data protection',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Network security',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and continuous security validation',
        metrics: 'Security validation',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'restaurant-pos-management': {
    keyBenefits: [
      {
        icon: Utensils,
        title: 'Intelligent POS & Ordering',
        description: 'Advanced point-of-sale system with mobile ordering, kitchen display integration, and payment processing',
        metrics: '99% order accuracy',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Menu Engineering Analytics',
        description: 'AI-powered menu optimisation with profitability analysis, ingredient costing, and popularity tracking',
        metrics: '25% profit optimisation',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Customer Loyalty & CRM',
        description: 'Integrated customer relationship management with loyalty programmes and personalised marketing',
        metrics: '80% customer retention',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Calendar,
        title: 'Table Management & Reservations',
        description: 'Intelligent table booking system with waitlist management, guest preferences, and dining experience optimisation',
        metrics: '95% table utilisation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Star,
        title: 'Staff Performance Analytics',
        description: 'Comprehensive staff tracking with performance metrics, scheduling optimisation, and tip management systems',
        metrics: '40% staff productivity',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Coffee,
        title: 'Online Delivery Integration',
        description: 'Multi-platform delivery management with commission tracking, driver coordination, and delivery analytics',
        metrics: '300% delivery growth',
        color: 'from-amber-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Inventory & Supply Management',
        description: 'Real-time inventory tracking with automated ordering, supplier management, and cost control',
        metrics: 'Zero stockouts',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Kitchen Operations Intelligence',
        description: 'Smart kitchen management with order routing, preparation timing, and quality control systems',
        metrics: 'Kitchen efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Restaurant Chain',
        description: 'Centralised restaurant chain management with standardised operations and consolidated reporting',
        metrics: 'Chain-wide operations',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Recipe & Cost Management',
        description: 'Advanced recipe management with portion control, nutritional analysis, and automated cost calculations',
        metrics: 'Recipe standardisation',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: CheckCircle,
        title: 'Compliance & Health Safety',
        description: 'Food safety compliance tracking with HACCP monitoring, allergen management, and health inspection readiness',
        metrics: 'Compliance excellence',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Financial Analytics Suite',
        description: 'Comprehensive financial reporting with profit analysis, tax management, and business intelligence dashboards',
        metrics: 'Financial insights',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive SOC 2, ISO 27001, and GDPR compliance with PCI DSS for payment processing security',
        metrics: 'Regulatory compliance',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat intelligence with performance monitoring and security posture optimization',
        metrics: 'Performance monitoring',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform with unlimited scalability and global deployment capabilities',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: 'Advanced protection',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart cards, and adaptive access controls',
        metrics: 'Identity security',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with automated threat detection, incident response, and forensic analysis',
        metrics: 'Threat detection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Data protection',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Network security',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and continuous security validation',
        metrics: 'Security validation',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'travel-booking-platform': {
    keyBenefits: [
      {
        icon: Plane,
        title: 'Comprehensive Travel Marketplace',
        description: 'Unified travel booking platform with flights, hotels, transfers, and experiences in one integrated system',
        metrics: '360° travel solutions',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'AI-Powered Travel Intelligence',
        description: 'Smart travel recommendations with price prediction, itinerary optimisation, and personalised suggestions',
        metrics: '90% booking satisfaction',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Traveller Experience Platform',
        description: 'Personalised travel experiences with preference tracking, trip management, and 24/7 support',
        metrics: 'Personalised travel excellence',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Corporate Travel Management',
        description: 'Enterprise travel solutions with policy enforcement, expense integration, and duty-of-care monitoring',
        metrics: '50% travel cost reduction',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Star,
        title: 'Travel Insurance & Protection',
        description: 'Comprehensive travel protection with insurance integration, disruption management, and emergency assistance',
        metrics: '100% travel protection',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: MapPin,
        title: 'Mobile Travel Companion',
        description: 'Complete mobile travel app with offline maps, real-time updates, and location-based recommendations',
        metrics: 'Travel companion excellence',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Global Supplier Network',
        description: 'Extensive supplier integration with real-time availability, competitive pricing, and quality standards',
        metrics: 'Global travel inventory',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Dynamic Pricing & Packaging',
        description: 'Intelligent pricing engine with dynamic packaging, group bookings, and corporate travel management',
        metrics: 'Optimised travel pricing',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Currency & Localisation',
        description: 'Global travel platform with multi-currency support, local payment methods, and regional content',
        metrics: 'Global travel accessibility',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Travel Expense Management',
        description: 'Automated expense tracking with receipt capture, policy compliance, and integrated accounting systems',
        metrics: 'Expense automation',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: CheckCircle,
        title: 'Booking Confirmation & Alerts',
        description: 'Real-time booking management with automated confirmations, schedule alerts, and change notifications',
        metrics: 'Booking reliability',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Travel Analytics & Reporting',
        description: 'Comprehensive travel analytics with spending insights, traveller behaviour analysis, and ROI tracking',
        metrics: 'Travel intelligence',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive SOC 2, ISO 27001, and GDPR compliance with travel industry data protection standards',
        metrics: 'Regulatory compliance',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat intelligence with performance monitoring and security posture optimization',
        metrics: 'Performance monitoring',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform with unlimited scalability and global deployment capabilities',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: 'Advanced protection',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart cards, and adaptive access controls',
        metrics: 'Identity security',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with automated threat detection, incident response, and forensic analysis',
        metrics: 'Threat detection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Data protection',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Network security',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and continuous security validation',
        metrics: 'Security validation',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'event-venue-management': {
    keyBenefits: [
      {
        icon: Calendar,
        title: 'Intelligent Event Scheduling',
        description: 'Advanced venue booking with conflict resolution, resource allocation, and automated event coordination',
        metrics: '100% scheduling accuracy',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Event Analytics & Optimisation',
        description: 'Comprehensive event analytics with attendance tracking, revenue optimisation, and performance insights',
        metrics: '40% revenue optimisation',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Client Experience Management',
        description: 'End-to-end client journey management with booking automation, communication tools, and service coordination',
        metrics: '95% client satisfaction',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Star,
        title: 'Event Marketing & Promotion',
        description: 'Integrated marketing tools with social media promotion, email campaigns, and ticket sales management',
        metrics: '200% marketing reach',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: Coffee,
        title: 'Vendor & Supplier Network',
        description: 'Comprehensive vendor management with preferred supplier networks, quote comparison, and contract management',
        metrics: '30% vendor cost savings',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: MapPin,
        title: 'Floor Planning & Layout',
        description: 'Interactive floor planning tools with 3D visualisation, capacity management, and safety compliance',
        metrics: 'Optimal space utilisation',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Resource & Equipment Management',
        description: 'Comprehensive resource tracking with equipment scheduling, maintenance management, and availability optimisation',
        metrics: 'Complete resource control',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Catering & Service Coordination',
        description: 'Integrated catering management with menu planning, service scheduling, and quality assurance',
        metrics: 'Service excellence',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'Multi-Venue Operations',
        description: 'Centralised management for multiple venues with consolidated booking and standardised operations',
        metrics: 'Multi-venue efficiency',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Audio Visual & Technology',
        description: 'Complete AV management with equipment booking, technical setup coordination, and live streaming capabilities',
        metrics: 'Technical excellence',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: CheckCircle,
        title: 'Event Security & Safety',
        description: 'Comprehensive security management with access control, emergency protocols, and safety compliance monitoring',
        metrics: 'Security assurance',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Post-Event Analytics',
        description: 'Detailed event analysis with attendee feedback, financial reporting, and performance benchmarking',
        metrics: 'Event insights',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive SOC 2, ISO 27001, and GDPR compliance with event industry data protection standards',
        metrics: 'Regulatory compliance',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat intelligence with performance monitoring and security posture optimization',
        metrics: 'Performance monitoring',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform with unlimited scalability and global deployment capabilities',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: 'Advanced protection',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart cards, and adaptive access controls',
        metrics: 'Identity security',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with automated threat detection, incident response, and forensic analysis',
        metrics: 'Threat detection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Data protection',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Network security',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and continuous security validation',
        metrics: 'Security validation',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'guest-experience-platform': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Omnichannel Guest Communication',
        description: 'Unified guest communication across all touchpoints with chat, SMS, email, and voice integration for seamless experience',
        metrics: '360° guest engagement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Personalisation Engine',
        description: 'AI-driven guest personalisation with preference learning, behavioural analytics, and customised service delivery',
        metrics: '95% guest satisfaction',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Star,
        title: 'Guest Journey Optimisation',
        description: 'End-to-end journey mapping with touchpoint optimisation, satisfaction tracking, and experience enhancement',
        metrics: 'Journey excellence',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Calendar,
        title: 'Service Request Management',
        description: 'Intelligent service request routing with automated assignment, priority management, and completion tracking',
        metrics: '15min average response',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Database,
        title: 'Guest Profile Intelligence',
        description: 'Comprehensive guest profiles with preference tracking, stay history, and predictive analytics for personalised service',
        metrics: 'Complete guest insights',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Feedback & Review Management',
        description: 'Real-time feedback collection with sentiment analysis, review response automation, and reputation monitoring',
        metrics: '4.8/5 review rating',
        color: 'from-pink-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Globe,
        title: 'Multi-Language Support',
        description: 'Global guest service with real-time translation, cultural adaptation, and localised communication preferences',
        metrics: '50+ languages supported',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Loyalty Program Integration',
        description: 'Advanced loyalty management with points tracking, tier benefits, and personalised rewards optimisation',
        metrics: 'Enhanced guest loyalty',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: CheckCircle,
        title: 'Mobile Guest Services',
        description: 'Complete mobile guest experience with app integration, push notifications, and location-based services',
        metrics: 'Mobile-first experience',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Guest Analytics Dashboard',
        description: 'Comprehensive guest analytics with satisfaction metrics, service performance, and experience insights',
        metrics: 'Data-driven insights',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'AI-Powered Recommendations',
        description: 'Intelligent service recommendations with upselling opportunities, activity suggestions, and personalised offers',
        metrics: 'Smart recommendations',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Coffee,
        title: 'Concierge Automation',
        description: 'Automated concierge services with local recommendations, booking assistance, and travel coordination',
        metrics: 'Concierge excellence',
        color: 'from-rose-500 to-pink-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive SOC 2, ISO 27001, and GDPR compliance with hospitality-specific data protection standards',
        metrics: 'Regulatory compliance',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat intelligence with performance monitoring and security posture optimization',
        metrics: 'Performance monitoring',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform with unlimited scalability and global deployment capabilities',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: 'Advanced protection',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart cards, and adaptive access controls',
        metrics: 'Identity security',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with automated threat detection, incident response, and forensic analysis',
        metrics: 'Threat detection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Data protection',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Network security',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and continuous security validation',
        metrics: 'Security validation',
        color: 'from-orange-500 to-red-600'
      }
    ]
  }
};
